import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogRef,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { PreInscricaoGrupo, PreInscricaoGrupoRequest } from '../../../core/api/pre-inscricao.dto';
import { PreInscricaoService } from '../../../core/api/pre-inscricao.service';
import { mensagemDeErro } from '../../../utils/mensagem-erro';

/**
 * Grupos por faixa etária de uma campanha, com o limite de vagas de cada um.
 *
 * Três recusas do backend viram mensagem aqui, e nenhuma delas é erro de sistema: faixas da
 * mesma campanha não podem se sobrepor; grupo com inscrito não se exclui; e o limite de vagas
 * não desce abaixo do que já está ocupado.
 */
@Component({
  selector: 'app-pre-inscricao-grupos-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
  ],
  templateUrl: './pre-inscricao-grupos.dialog.html',
  styleUrl: './pre-inscricao-grupos.dialog.scss',
})
export class PreInscricaoGruposDialogComponent implements OnInit {
  @Input() campanhaId = '';
  @Input() campanhaNome?: string;

  grupos: PreInscricaoGrupo[] = [];
  carregando = false;
  salvando = false;

  editandoId: string | null = null;
  nome = '';
  idadeMinima: number | null = null;
  idadeMaxima: number | null = null;
  limiteVagas: number | null = null;

  constructor(
    private ref: NbDialogRef<PreInscricaoGruposDialogComponent>,
    private service: PreInscricaoService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.carregar();
  }

  carregar(): void {
    if (!this.campanhaId) return;
    this.carregando = true;
    this.service
      .listarGrupos(this.campanhaId)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (gs) => (this.grupos = gs ?? []),
        error: (err) => {
          this.grupos = [];
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar os grupos.'), 'Erro');
        },
      });
  }

  editar(g: PreInscricaoGrupo): void {
    this.editandoId = g.id ?? null;
    this.nome = g.nome ?? '';
    this.idadeMinima = g.idadeMinima ?? null;
    this.idadeMaxima = g.idadeMaxima ?? null;
    this.limiteVagas = g.limiteVagas ?? null;
  }

  cancelarEdicao(): void {
    this.editandoId = null;
    this.limparFormulario();
  }

  salvar(): void {
    const nome = this.nome.trim();
    if (!nome || this.idadeMinima == null || this.idadeMaxima == null || this.limiteVagas == null) {
      this.toastr.warning('Nome, faixa etária e limite de vagas são obrigatórios.', 'Campos obrigatórios');
      return;
    }
    if (this.idadeMaxima < this.idadeMinima) {
      this.toastr.warning('A idade máxima não pode ser menor que a mínima.', 'Faixa inválida');
      return;
    }

    const corpo: PreInscricaoGrupoRequest = {
      nome,
      idadeMinima: this.idadeMinima,
      idadeMaxima: this.idadeMaxima,
      limiteVagas: this.limiteVagas,
    };

    this.salvando = true;
    const req = this.editandoId
      ? this.service.atualizarGrupo(this.campanhaId, this.editandoId, { ...corpo, id: this.editandoId })
      : this.service.criarGrupo(this.campanhaId, corpo);

    req.pipe(finalize(() => (this.salvando = false))).subscribe({
      next: () => {
        this.toastr.success(this.editandoId ? 'Grupo atualizado.' : nome + ' adicionado.', 'Grupos');
        this.editandoId = null;
        this.limparFormulario();
        this.carregar();
      },
      // Faixa sobreposta e limite abaixo do ocupado chegam como 400 com a mensagem do backend,
      // que nomeia o grupo em conflito — ela é melhor do que qualquer texto nosso.
      error: (err) => this.toastr.danger(mensagemDeErro(err, 'Não foi possível salvar o grupo.'), 'Erro'),
    });
  }

  excluir(g: PreInscricaoGrupo): void {
    if (!g.id) return;
    this.service.excluirGrupo(this.campanhaId, g.id).subscribe({
      next: () => {
        this.toastr.success(g.nome + ' excluído.', 'Grupos');
        this.carregar();
      },
      error: (err) => this.toastr.danger(mensagemDeErro(err, 'Não foi possível excluir o grupo.'), 'Erro'),
    });
  }

  fechar(): void {
    this.ref.close();
  }

  private limparFormulario(): void {
    this.nome = '';
    this.idadeMinima = null;
    this.idadeMaxima = null;
    this.limiteVagas = null;
  }
}
